import React, { useContext } from 'react';
import HeroSection from '../components/HeroSection';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ModalContext } from '../App';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../components/ServicePageLayout.css';

const TrademarkSearch = () => {
  const { openModal } = useContext(ModalContext);
  const [openFaq, setOpenFaq] = React.useState(null);

  const heroData = {
    eyebrow: 'TRADEMARK SEARCH',
    title: 'Ensure Your Mark is Unique',
    description: 'Comprehensive trademark search services to identify potential conflicts before you invest in filing your application.',
    backgroundImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200',
    showForm: false,
    ctaText: 'Start Your Search',
    ctaLink: '#pricing'
  };

  const processSteps = [
    {
      title: 'Initial Consultation',
      description: 'We discuss your proposed trademark, your business activities, and intended markets. Our trademark specialists gather comprehensive information about your mark, including any variations you are considering and specific goods or services you will offer.'
    },
    {
      title: 'Comprehensive Search',
      description: 'Our experienced team conducts an exhaustive search across multiple databases including USPTO federal registrations, state trademark registrations, common law usage, and domain names. We analyze potential conflicts and assess risk levels for your proposed mark.'
    },
    {
      title: 'Detailed Report & Recommendations',
      description: 'You receive a comprehensive written report outlining our findings, potential conflicts, and professional recommendations. We provide strategic guidance on how to proceed with your trademark application, including modifications to strengthen your mark if necessary.'
    }
  ];

  const pricingPlans = [
    { 
      name: 'Basic Search', 
      price: '149', 
      features: [
        'Direct Hit Search',
        'USPTO Database Search',
        'Common Law Search',
        'Search Report',
        'Risk Assessment'
      ] 
    },
    { 
      name: 'Comprehensive Search', 
      price: '299', 
      features: [
        'Direct Hit Search',
        'USPTO Database Search',
        'Common Law Search',
        'State Trademark Search',
        'Domain Name Search',
        'Detailed Analysis Report',
        'Strategic Recommendations'
      ], 
      isPopular: true 
    },
    { 
      name: 'International Search', 
      price: '499', 
      features: [
        'U.S. Comprehensive Search',
        'International Database Search',
        'Madrid Protocol Countries',
        'Foreign Trademark Databases',
        'Global Risk Assessment',
        'Multi-Jurisdiction Report',
        'International Filing Strategy'
      ] 
    }
  ];

  const whyChooseData = {
    paragraphs: [
      'A comprehensive trademark search is a critical first step in protecting your brand. At Unity Trademark, our experienced search specialists utilize advanced search methodologies and access to multiple trademark databases to provide you with the most thorough analysis available. We go beyond basic database searches to investigate common law usage, domain names, and business directories that could present potential conflicts.',
      'Our detailed search reports provide you with the information you need to make informed decisions about your trademark application. We assess the likelihood of approval, identify potential obstacles, and provide strategic recommendations to strengthen your mark. Many costly rejections and legal disputes can be avoided with a proper trademark search conducted before filing your application.'
    ],
    features: [
      {
        title: 'Comprehensive Coverage',
        description: 'We search federal and state databases, common law usage, domain registrations, and business directories to identify any potential conflicts with your proposed mark.'
      },
      {
        title: 'Expert Analysis',
        description: 'Our experienced trademark specialists analyze search results and provide professional risk assessments with strategic recommendations for your specific situation.'
      },
      {
        title: 'Detailed Reports',
        description: 'You receive a comprehensive written report outlining all findings, potential conflicts, and clear recommendations for how to proceed with your trademark application.'
      },
      {
        title: 'Fast Turnaround',
        description: 'Most trademark searches are completed within 2-3 business days, allowing you to move forward with your branding and filing decisions quickly and confidently.'
      }
    ]
  };

  const testimonials = [
    {
      name: 'Jennifer Martinez',
      company: 'Tech Startup Founder',
      text: 'The comprehensive search saved me from a costly mistake. They found a conflicting mark I never would have discovered on my own.',
      image: 'https://i.pravatar.cc/150?img=45'
    },
    {
      name: 'Robert Taylor',
      company: 'Restaurant Owner',
      text: 'Their detailed report and recommendations helped me modify my mark to avoid potential conflicts. Worth every penny!',
      image: 'https://i.pravatar.cc/150?img=33'
    },
    {
      name: 'Amanda Chen',
      company: 'Fashion Designer',
      text: 'Professional, thorough, and fast. The search gave me confidence to move forward with my brand.',
      image: 'https://i.pravatar.cc/150?img=16'
    }
  ];

  const faqs = [
    {
      question: 'Why is a trademark search necessary?',
      answer: 'A trademark search helps identify existing marks that could conflict with yours, reducing the risk of rejection by the USPTO and avoiding potential legal disputes. It saves time and money by revealing potential issues before you invest in filing and building your brand.'
    },
    {
      question: 'How long does a trademark search take?',
      answer: 'Most comprehensive trademark searches are completed within 2-3 business days. International searches may take slightly longer depending on the countries involved.'
    },
    {
      question: 'What databases do you search?',
      answer: 'We search USPTO federal registrations, state trademark databases, common law sources, domain name registrations, business directories, and international databases depending on your selected search package.'
    },
    {
      question: 'Can I file without a search?',
      answer: 'While not legally required, filing without a search is risky. The USPTO will reject applications that conflict with existing marks, and you could face legal action from trademark holders. A search significantly improves your chances of success.'
    },
    {
      question: 'What if the search finds conflicts?',
      answer: 'We provide strategic recommendations which may include modifying your mark, choosing a different mark, or proceeding with calculated risk if conflicts are minimal. Our detailed analysis helps you make an informed decision.'
    }
  ];

  return (
    <div className="service-page">
      <HeroSection {...heroData} />

      <section className="section">
        <div className="container">
          <h2 className="section-title">About Trademark Search Services</h2>
          <div className="about-content">
            <p className="about-description">
              A comprehensive trademark search is your first line of defense against costly rejections and legal disputes. Before investing time and resources into building your brand, it is essential to ensure your proposed trademark is available for registration and use. Our professional trademark search services utilize advanced search techniques and access to comprehensive databases to identify potential conflicts and assess the viability of your mark.
            </p>
            <p className="about-description">
              Our experienced trademark specialists go beyond simple database queries to conduct thorough investigations across federal registrations, state filings, common law usage, domain names, and business directories. We provide detailed analysis and strategic recommendations, helping you make informed decisions about your trademark application and brand protection strategy. With our comprehensive search services, you can proceed with confidence knowing you have minimized the risk of rejection and future legal challenges.
            </p>
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            Our streamlined three-step search process ensures you receive comprehensive results and expert analysis quickly and efficiently.
          </p>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="section">
        <div className="container">
          <h2 className="section-title">Choose Your Search Package</h2>
          <p className="section-subtitle">
            Select the trademark search package that matches your coverage needs and business goals. All packages include professional analysis, detailed reports, and strategic recommendations.
          </p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <PricingCard 
                key={index} 
                {...plan} 
                onGetStarted={(packageName) => openModal(`Trademark Search - ${packageName}`)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Why Choose Unity Trademark</h2>
          <div className="why-choose-content">
            {whyChooseData.paragraphs.map((paragraph, index) => (
              <p key={index} className="why-choose-paragraph">{paragraph}</p>
            ))}
          </div>
          <div className="features-grid">
            {whyChooseData.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <CheckCircle size={40} className="feature-icon-svg" />
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Search Your Trademark?</h2>
            <p className="cta-description">
              Get comprehensive trademark search results starting at just $149. Protect your brand investment with professional research and expert analysis from Unity Trademark.
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => openModal('Trademark Search')}
            >
              Start Your Trademark Search
            </Button>
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Client Success Stories</h2>
          <div className="testimonials-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 25 },
                1024: { slidesPerView: 3, spaceBetween: 30 },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard {...testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrademarkSearch;